/**
 * HttpClient — platform-free HTTP client.
 *
 * CP1 compliance:
 *  - No `fetch` / `window` / `document` / `navigator` / `location` globals.
 *  - Fetch is injected via `FetchAdapter` at construction. The web app supplies
 *    `window.fetch.bind(window)`; the native app will supply whatever it has.
 *  - Token access + mutation is injected via `TokenProvider`. Where tokens
 *    live (localStorage on web, secure keystore on native) is T7's concern —
 *    not this file's.
 *
 * Behaviors ported from legacy `frontend/src/services/apiClient.ts`:
 *  - `Authorization: Bearer <access>` added when a token exists.
 *  - On 401 (non-auth endpoint, not already retried): call `/api/auth/refresh`,
 *    retry the original request once with the new access token.
 *  - Concurrent 401s share a single refresh promise (avoid thundering-herd).
 *  - On refresh failure: clear tokens via `TokenProvider.clearTokens()` so the
 *    app can react by routing the user back to login.
 *  - `/api/auth/refresh` and `/api/auth/google` are never retried — they are
 *    the refresh mechanism itself, and retrying them on 401 would loop.
 */

import { z } from 'zod';
import {
  ApiError,
  MissingFetchAdapterError,
  ResponseValidationError,
} from './errors';

// ---------------------------------------------------------------------------
// Injected dependencies
// ---------------------------------------------------------------------------

/**
 * Typed fetch adapter — structurally compatible with the platform `fetch`.
 *
 * Declared as a type-position reference only; ESLint's `no-restricted-globals`
 * catches value references, and TypeScript will widen / narrow this at the
 * call site. This keeps the signature familiar without pulling DOM lib into
 * the shared tsconfig.
 */
export type FetchAdapter = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    credentials?: 'include' | 'omit' | 'same-origin';
    /**
     * Abort signal bounding the request (BUG43). The platform `fetch` and the
     * native adapter both honour it; a timeout rejects the fetch so a stalled
     * backend surfaces an inline error instead of an infinite spinner. Declared
     * structurally to keep the shared tsconfig DOM-free — the browser's
     * `AbortSignal` satisfies it.
     */
    signal?: { readonly aborted: boolean };
  }
) => Promise<FetchResponse>;

/**
 * Minimal typed shape of the Response object the fetch adapter returns. We
 * declare our own to keep the shared tsconfig DOM-free. The browser's
 * `Response` satisfies this structurally.
 */
export interface FetchResponse {
  ok: boolean;
  status: number;
  statusText: string;
  headers: { get(name: string): string | null };
  json(): Promise<unknown>;
  text(): Promise<string>;
}

/**
 * Contract the HttpClient uses to read + mutate tokens. The web adapter will
 * read from localStorage; the native adapter will read from a secure keystore.
 *
 * Refresh-token accessors (`getRefreshToken` / `setRefreshToken`) are OPTIONAL
 * and native-only (T27.2.2.2). On web the refresh token is delivered as an
 * httpOnly `sameSite:'lax'` cookie and is never readable by JS — the web
 * adapter leaves both undefined and the client keeps the cookie path
 * byte-for-byte (`credentials:'include'`, no `X-Refresh-Token` header). In the
 * native WebView that cookie is cross-site and drops, so the native adapter
 * stores the rotated refresh token in the Android Keystore and the client sends
 * it explicitly on `/refresh` (header in) and persists the rotation (body out).
 */
export interface TokenProvider {
  getAccessToken(): string | null;
  setAccessToken(token: string | null): void;
  /** Called on refresh failure — signals "user must re-authenticate". */
  clearTokens(): void;
  /**
   * Native-only. Read the stored refresh token so the client can send it as the
   * `X-Refresh-Token` header on `/refresh`. Web adapters leave this undefined —
   * the cookie carries the token and the header is never sent.
   */
  getRefreshToken?(): string | null;
  /**
   * Native-only. Persist the rotated refresh token returned in the `/refresh` /
   * `/api/auth/google` response body. Web adapters leave this undefined.
   *
   * Returns `void` on a fire-and-forget adapter or a `Promise<void>` on a
   * durable adapter (T27.2.2.2): the native Keystore adapter returns the
   * promise that resolves once the rotated token is on disk so the refresh path
   * can `await` it before resolving — the next request never reads a spent
   * token, and the FIFO write queue drains the queued access-token write too.
   */
  setRefreshToken?(token: string | null): void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Constructor config
// ---------------------------------------------------------------------------

/**
 * Injected mutex used to serialize the network refresh across the whole
 * origin. The web adapter backs this with the Web Locks API
 * (`navigator.locks.request(name, fn)`), which queues by default and is
 * shared across every `HttpClient` instance in a tab *and* across tabs of the
 * same origin. Tests inject a real promise-chain mutex; when omitted, the
 * client falls back to a pass-through that runs `fn` immediately (no
 * serialization — preserves the previous behavior for the native adapter and
 * any caller that doesn't supply one).
 *
 * Kept injected to keep the shared package CP1 / DOM-free — same pattern as
 * the injected `fetch`. `navigator` is never referenced here.
 */
export type RefreshLock = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

export interface HttpClientConfig {
  /** Absolute base URL, e.g. `https://api.example.com`. No trailing slash. */
  baseUrl: string;
  /** Injected fetch adapter. In web, pass `window.fetch.bind(window)`. */
  fetch: FetchAdapter;
  /** Injected token provider (see interface above). */
  tokens: TokenProvider;
  /**
   * When true (default), cross-origin requests include the refresh-token
   * cookie via `credentials: 'include'`. Legacy behavior — required because
   * the refresh token is delivered as an HttpOnly cookie.
   */
  withCredentials?: boolean;
  /**
   * Optional refresh mutex (see `RefreshLock`). Defaults to a pass-through
   * `(_, fn) => fn()` so callers that don't inject one behave exactly as
   * before. The web adapter injects a `navigator.locks`-backed impl so only
   * ONE `/api/auth/refresh` runs per origin per token-expiry — BUG15.
   */
  lock?: RefreshLock;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

const AUTH_REFRESH_PATH = '/api/auth/refresh';
const AUTH_GOOGLE_PATH = '/api/auth/google';

/**
 * Ceiling on a single request's fetch (BUG43). Set comfortably above the 8s
 * server-side Turnstile `siteverify` budget so a slow-but-answering backend is
 * never cut off, while a truly stalled backend still rejects instead of leaving
 * a mutation pending forever (e.g. the register "Creating account…" spinner).
 */
const REQUEST_TIMEOUT_MS = 30000;

/**
 * Minimal structural shapes for the abort + timer primitives, declared locally
 * so `packages/shared/**` needs no DOM/Node lib (CP1). Both `AbortController`
 * and the timer functions exist on every JS runtime (browser, Node, the native
 * WebView); we read them off `globalThis` rather than referencing bare globals,
 * keeping the shared tsconfig (`lib: ['ES2023']`) and the CP1 global-ban clean.
 */
interface AbortControllerLike {
  readonly signal: { readonly aborted: boolean };
  abort(): void;
}

const runtime = globalThis as unknown as {
  AbortController: new () => AbortControllerLike;
  setTimeout: (handler: () => void, timeout: number) => unknown;
  clearTimeout: (id: unknown) => void;
};

/**
 * Single per-origin lock name shared by every `HttpClient` instance (and every
 * tab). Backed by the Web Locks API on web, this serializes the network
 * refresh so two HttpClients (or two tabs) never both fire
 * `/api/auth/refresh` against the same rotating refresh cookie — BUG15.
 */
export const AUTH_REFRESH_LOCK_NAME = 'shopista:auth-refresh';

/** Pass-through used when no `lock` is injected — runs `fn` immediately. */
const passThroughLock: RefreshLock = (_name, fn) => fn();

/**
 * Event name dispatched on `window` when token refresh fails and the user's
 * session has been cleared. Subscribed to at the app layer (apps/web/src/main.tsx)
 * to trigger a redirect to /login and clear cached query data. Defined here
 * as a cross-package contract — the shared HTTP client emits, the app reacts.
 */
export const SESSION_CLEARED_EVENT = 'shopista:session-cleared';

/**
 * `sessionToken` (the rotated access JWT) is always present. `refreshToken` is
 * native-only and additive (T27.2.2.2): the backend emits it in the body only
 * when the caller signalled native via the `X-Refresh-Token` header, so the web
 * cookie path never sees it. When present the client persists it via
 * `setRefreshToken` so the next refresh sends the rotated token.
 */
const refreshResponseBodySchema = z.object({
  sessionToken: z.string(),
  refreshToken: z.string().optional(),
});

/** Native refresh-token transport header (T27.2.2.2). */
export const REFRESH_TOKEN_HEADER = 'X-Refresh-Token';

type InternalRequest = {
  method: string;
  path: string;
  responseType?: 'json' | 'text';
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /** Used to flag retry attempts so we never loop. */
  _retry?: boolean;
};

export class HttpClient {
  private readonly baseUrl: string;
  private readonly fetch: FetchAdapter;
  private readonly tokens: TokenProvider;
  private readonly withCredentials: boolean;
  private readonly lock: RefreshLock;

  /**
   * Shared refresh promise — when N requests 401 simultaneously, they all
   * await the same refresh call rather than each firing their own.
   */
  private refreshPromise: Promise<string | null> | null = null;

  constructor(config: HttpClientConfig) {
    if (!config.fetch) {
      throw new MissingFetchAdapterError();
    }
    // Trim trailing slash so `${baseUrl}${path}` never double-slashes.
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.fetch = config.fetch;
    this.tokens = config.tokens;
    this.withCredentials = config.withCredentials !== false;
    this.lock = config.lock ?? passThroughLock;
  }

  // -------------------------------------------------------------------------
  // Public request helpers
  // -------------------------------------------------------------------------

  async get<T>(
    path: string,
    schema: z.ZodType<T>,
    query?: InternalRequest['query']
  ): Promise<T> {
    const req: InternalRequest =
      query !== undefined
        ? { method: 'GET', path, query }
        : { method: 'GET', path };
    return this.requestWithSchema<T>(req, schema);
  }

  async post<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>
  ): Promise<T> {
    return this.requestWithSchema<T>({ method: 'POST', path, body }, schema);
  }

  async put<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>
  ): Promise<T> {
    return this.requestWithSchema<T>({ method: 'PUT', path, body }, schema);
  }

  async patch<T>(
    path: string,
    body: unknown,
    schema: z.ZodType<T>
  ): Promise<T> {
    return this.requestWithSchema<T>({ method: 'PATCH', path, body }, schema);
  }

  /**
   * DELETE returns nothing typed — backend responds with `{}` or 204. We
   * accept either and return void.
   */
  async delete(path: string): Promise<void> {
    await this.request({ method: 'DELETE', path });
  }

  /**
   * POST that does NOT validate a response. Use when the backend returns
   * an empty body / 204 / we intentionally ignore the body.
   */
  async postVoid(path: string, body?: unknown): Promise<void> {
    await this.request({ method: 'POST', path, body });
  }

  /**
   * POST that returns the unparsed JSON body without Zod validation.
   *
   * Use when the caller validates the response upstream (e.g. the sync
   * engine, where `SyncResponseV2` is shaped by the backend Zod schema in
   * `syncV2.ts` and the engine treats the response as a contract type).
   * Forwards the same 401-refresh-retry pipeline as `post<T>` — refresh-
   * on-401 is the load-bearing reason for routing through HttpClient.
   */
  async postRaw(path: string, body: unknown): Promise<unknown> {
    return this.request({ method: 'POST', path, body });
  }

  /** PATCH that returns the unparsed JSON body. */
  async patchRaw(path: string, body: unknown): Promise<unknown> {
    return this.request({ method: 'PATCH', path, body });
  }

  /** PUT that returns the unparsed JSON body. */
  async putRaw(path: string, body: unknown): Promise<unknown> {
    return this.request({ method: 'PUT', path, body });
  }

  /** DELETE that returns the unparsed JSON body. */
  async deleteRaw(path: string): Promise<unknown> {
    return this.request({ method: 'DELETE', path });
  }

  /**
   * GET that returns the unparsed JSON body without Zod validation. See
   * `postRaw` for rationale.
   */
  async getRaw(
    path: string,
    query?: InternalRequest['query']
  ): Promise<unknown> {
    const req: InternalRequest =
      query !== undefined
        ? { method: 'GET', path, query }
        : { method: 'GET', path };
    return this.request(req);
  }

  /** GET text while retaining the normal auth, timeout, and 401 refresh path. */
  async getText(path: string): Promise<string> {
    return this.request({ method: 'GET', path, responseType: 'text' }) as Promise<string>;
  }

  /**
   * Public refresh handle — delegates to the private `refreshAccessToken()`
   * so non-REST transports (the SSE subscriber) can converge on the same
   * deduped refresh path. Because it shares the single `refreshPromise`, a
   * concurrent REST 401 refresh and an SSE reconnect resolve from one
   * `/api/auth/refresh` call. Returns the new access token, or `null` when
   * the refresh failed (tokens are cleared + `shopista:session-cleared`
   * dispatched inside `failRefresh`).
   */
  refresh(): Promise<string | null> {
    return this.refreshAccessToken();
  }

  // -------------------------------------------------------------------------
  // Request pipeline
  // -------------------------------------------------------------------------

  private async requestWithSchema<T>(
    req: InternalRequest,
    schema: z.ZodType<T>
  ): Promise<T> {
    const raw = await this.request(req);
    const parseResult = schema.safeParse(raw);
    if (!parseResult.success) {
      throw new ResponseValidationError({
        url: this.buildUrl(req.path, req.query),
        method: req.method,
        zodError: parseResult.error,
        received: raw,
      });
    }
    return parseResult.data;
  }

  /**
   * Low-level request. Returns parsed JSON (or undefined for empty bodies).
   * Throws ApiError on non-2xx. Handles 401-refresh-retry.
   */
  private async request(req: InternalRequest): Promise<unknown> {
    const url = this.buildUrl(req.path, req.query);
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    const accessToken = this.tokens.getAccessToken();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const init: Parameters<FetchAdapter>[1] = {
      method: req.method,
      headers,
      credentials: this.withCredentials ? 'include' : 'same-origin',
    };

    if (req.body !== undefined && req.body !== null) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(req.body);
    }

    // Bound the fetch so a stalled backend rejects instead of hanging forever
    // (BUG43). A fresh controller/signal is created per `request()` call — the
    // 401 refresh-retry path recurses through `request()`, so the retried
    // request gets its own controller and is never aborted by this one's timer.
    const controller = new runtime.AbortController();
    init.signal = controller.signal;
    const timeoutId = runtime.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let response: FetchResponse;
    try {
      response = await this.fetch(url, init);
    } finally {
      runtime.clearTimeout(timeoutId);
    }

    // ---- 401 refresh-and-retry ----
    if (
      response.status === 401 &&
      !req._retry &&
      !req.path.includes(AUTH_REFRESH_PATH) &&
      !req.path.includes(AUTH_GOOGLE_PATH)
    ) {
      // If we have no access token at all, there's nothing to refresh toward.
      // Fall through to the normal error path — caller decides what to do
      // (typically: route to login).
      if (accessToken) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          return this.request({ ...req, _retry: true });
        }
      }
    }

    if (!response.ok) {
      let errorBody: unknown = null;
      try {
        errorBody = await response.json();
      } catch {
        try {
          errorBody = await response.text();
        } catch {
          errorBody = null;
        }
      }
      throw new ApiError({
        message: `${req.method} ${url} failed: ${response.status} ${response.statusText}`,
        status: response.status,
        url,
        method: req.method,
        body: errorBody,
      });
    }

    // 204 No Content / empty body tolerance: let callers treat undefined as ok.
    if (response.status === 204) {
      return undefined;
    }

    if (req.responseType === 'text') return response.text();

    // Parse body. If parsing throws (empty body on a 200), treat as undefined.
    try {
      return await response.json();
    } catch {
      return undefined;
    }
  }


  /**
   * Refresh the access token, serialized per-origin so only ONE network
   * `/api/auth/refresh` runs per token-expiry across every HttpClient instance
   * and every tab (BUG15).
   *
   * Two layers of dedup:
   *  1. **Instance-level `refreshPromise`** — collapses simultaneous 401s
   *     *within one instance* so a single instance never queues on the
   *     cross-origin lock twice.
   *  2. **Injected `lock`** (Web Locks on web) — serializes the *network*
   *     refresh across instances + tabs. Inside the lock, the holder first
   *     re-reads the stored token: if a prior lock-holder already refreshed
   *     (token changed from the pre-lock snapshot), it returns that token and
   *     skips the network call — so the rotating refresh cookie is spent by
   *     exactly one POST. Without serialization (pass-through lock) this is a
   *     no-op and behavior matches the legacy single-shot refresh.
   */
  private refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Snapshot the token we're refreshing *from*, taken BEFORE we acquire the
    // lock. Inside the critical section we compare against this to detect a
    // prior holder that already rotated a fresh token in.
    const tokenBeforeRefresh = this.tokens.getAccessToken();

    this.refreshPromise = this.lock(AUTH_REFRESH_LOCK_NAME, async () => {
      // Critical section — we now hold the per-origin lock. Any prior holder
      // has provably finished (its network refresh resolved + persisted)
      // before releasing, so this re-read sees the winner's token if one ran.
      const tokenInLock = this.tokens.getAccessToken();
      if (tokenInLock !== null && tokenInLock !== tokenBeforeRefresh) {
        // A prior lock-holder already refreshed — reuse its token, skip the
        // (now rotation-losing) network call entirely. This is
        // `recoverOrFail`'s comparison, run while holding the lock so the
        // winner is guaranteed to have persisted.
        return tokenInLock;
      }

      const url = this.buildUrl(AUTH_REFRESH_PATH);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      // Native-only (T27.2.2.2): the WebView cookie is cross-site and drops, so
      // we read the refresh token from the secure store and send it explicitly.
      // Presence of the header is the backend's "native caller" signal. On web
      // `getRefreshToken` is undefined → no header → cookie path unchanged.
      const storedRefreshToken = this.tokens.getRefreshToken?.() ?? null;
      if (storedRefreshToken) {
        headers[REFRESH_TOKEN_HEADER] = storedRefreshToken;
      }

      // Bound the refresh fetch with the same ceiling as `request()` (BUG43).
      // `refreshAccessToken` calls `this.fetch` directly (not through
      // `request()`), so without this a hung `/api/auth/refresh` is the same
      // forever-hang on the authenticated path. A fresh controller per refresh;
      // the timer is cleared in the `finally` regardless of outcome. On
      // abort/timeout the fetch rejects and lands in the `catch` below, which
      // routes through `recoverOrFail(tokenBeforeRefresh, null)` — the transient
      // branch that KEEPS tokens (a hung refresh is a network blip, not an auth
      // rejection, so it must never force a spurious logout).
      const controller = new runtime.AbortController();
      const timeoutId = runtime.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS
      );
      try {
        let response: FetchResponse;
        try {
          response = await this.fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({}),
            credentials: this.withCredentials ? 'include' : 'same-origin',
            signal: controller.signal,
          });
        } finally {
          runtime.clearTimeout(timeoutId);
        }
        if (!response.ok) {
          // Discriminate the failure (T27.2.2.2): a `5xx` is the backend
          // bouncing (redeploy / transient) — keep tokens, surface a retryable
          // error. Every definitive `4xx` (`401`/`403` and the backend's
          // `400 No active household` / `404 User not found` for dead sessions)
          // clears, so we log out instead of looping against a dead session.
          return this.recoverOrFail(tokenBeforeRefresh, response.status);
        }
        const raw = await response.json();
        const parsed = refreshResponseBodySchema.safeParse(raw);
        if (!parsed.success) {
          // A validation error is a transient backend-shape blip, not an auth
          // rejection — keep tokens (status `null` = the transient branch).
          return this.recoverOrFail(tokenBeforeRefresh, null);
        }
        this.tokens.setAccessToken(parsed.data.sessionToken);
        // Persist the rotated refresh token when the backend returned one (native
        // body-out path). Single-use rotation means the NEXT refresh must send
        // this value, not the spent one. Web bodies omit it → no-op.
        //
        // `await` the durable write LAST so a `Promise<void>` adapter (the native
        // Keystore) has the rotated token on disk before this refresh resolves —
        // and because the durable writes share one FIFO queue, awaiting this tail
        // also drains the queued access-token write. A `void`-returning adapter
        // (web) makes the await a no-op.
        if (parsed.data.refreshToken !== undefined) {
          await this.tokens.setRefreshToken?.(parsed.data.refreshToken);
        }
        return parsed.data.sessionToken;
      } catch {
        // A network throw (dropped WebView connection mid-redeploy) is transient
        // — keep tokens (status `null` = the transient branch).
        return this.recoverOrFail(tokenBeforeRefresh, null);
      }
    }).finally(() => {
      // Always release the shared promise so the next 401 starts fresh.
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  /**
   * Decide what a *failed* refresh means.
   *
   * The refresh token rotates on every `/api/auth/refresh` call (single-use,
   * for stolen-token detection). When two refreshers (this tab's two HttpClient
   * instances, or two tabs) refresh against the same cookie at once, the loser
   * presents a now-dead rotated token and gets a 401 — even though the session
   * is perfectly alive and the winner already stored a fresh access token.
   *
   * So before treating a refresh-401 as session death, re-read the stored
   * access token: if it is non-null *and* changed from the snapshot taken
   * before our refresh POST, a concurrent refresher already won. Return that
   * new token so the original request retries with it — no logout.
   *
   * Otherwise the outcome depends on WHY the refresh failed (T27.2.2.2):
   *  - `status` is a definitive `4xx` (`400`/`401`/`403`/`404`) → the session is
   *    genuinely dead → `failRefresh` (clears tokens + emits
   *    `shopista:session-cleared`), preserving the BUG7 redirect-to-/login.
   *  - `status` is `5xx`, or `null` (a network throw / response-validation
   *    error) → the backend is transiently unavailable, NOT an auth rejection →
   *    return `null` WITHOUT clearing tokens. The original request surfaces a
   *    retryable error and a later `/refresh` succeeds — on native this is what
   *    stops a redeploy / network blip from durably wiping the Keystore refresh
   *    token and forcing re-login.
   */
  private recoverOrFail(
    tokenBeforeRefresh: string | null,
    status: number | null
  ): string | null {
    const current = this.tokens.getAccessToken();
    if (current !== null && current !== tokenBeforeRefresh) {
      return current;
    }
    // Transient: a `5xx`, or a network/validation failure (status === null).
    // Keep tokens — do not clear, do not emit session-cleared.
    if (status === null || status >= 500) {
      return null;
    }
    // Every definitive `4xx` → genuine rejection → clear + redirect.
    return this.failRefresh();
  }

  /**
   * Consolidates the "refresh failed → user must re-authenticate" branches.
   * Clears tokens and, when running in a browser, dispatches
   * `shopista:session-cleared` on `window` so the app can synchronously route
   * to /login. SSR-safe: feature-checks `window` + `dispatchEvent` + `CustomEvent`
   * to keep the shared package free of DOM lib types.
   */
  private failRefresh(): null {
    this.tokens.clearTokens();
    const g = globalThis as unknown as {
      window?: {
        dispatchEvent?: (event: unknown) => boolean;
        CustomEvent?: new (type: string, init?: unknown) => unknown;
      };
    };
    const w = g.window;
    if (w && typeof w.dispatchEvent === 'function' && typeof w.CustomEvent === 'function') {
      try {
        w.dispatchEvent(new w.CustomEvent(SESSION_CLEARED_EVENT));
      } catch {
        // Never let a listener / dispatch error mask the refresh failure.
      }
    }
    return null;
  }

  private buildUrl(
    path: string,
    query?: InternalRequest['query']
  ): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const qs = query ? serializeQuery(query) : '';
    return `${this.baseUrl}${normalizedPath}${qs}`;
  }
}

function serializeQuery(
  query: Record<string, string | number | boolean | undefined | null>
): string {
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return pairs.length ? `?${pairs.join('&')}` : '';
}
